import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Calendar, CheckCircle, Clock, PencilSimple, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';

const withAuth = () => ({ withCredentials: true });

// Build a list of the next N Mondays (weekly drop slots) starting from this Monday.
const buildWeekSlots = (n = 12) => {
  const today = new Date();
  const day = today.getDay(); // 0 = Sun, 1 = Mon
  const offsetToMonday = day === 0 ? 1 : (day === 1 ? 0 : 8 - day);
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  start.setDate(today.getDate() + offsetToMonday);
  const slots = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + (i * 7));
    slots.push(d.toISOString().slice(0, 10)); // YYYY-MM-DD
  }
  return slots;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const BlogCalendarPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/blog/admin/posts`, withAuth());
      setPosts(r.data.posts || []);
    } catch (e) {
      toast.error(e.response?.status === 403 ? 'Admin only' : 'Could not load');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setReleaseDate = async (slug, date) => {
    try {
      await axios.put(`${API_URL}/api/blog/admin/posts/${slug}`, { planned_release_date: date || null }, withAuth());
      toast.success(date ? `Scheduled for ${fmtDate(date)}` : 'Cleared');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const publish = async (slug) => {
    try {
      await axios.put(`${API_URL}/api/blog/admin/posts/${slug}`, { published: true }, withAuth());
      toast.success('Published — live on blog');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    }
  };

  // Group posts into 3 buckets
  const { drafts, scheduled, published } = useMemo(() => {
    const d = [], s = [], p = [];
    posts.forEach(post => {
      if (post.published) p.push(post);
      else if (post.planned_release_date) s.push(post);
      else d.push(post);
    });
    // Sort scheduled by date ascending
    s.sort((a, b) => (a.planned_release_date || '').localeCompare(b.planned_release_date || ''));
    // Sort published newest first
    p.sort((a, b) => (b.published_at || '').localeCompare(a.published_at || ''));
    return { drafts: d, scheduled: s, published: p };
  }, [posts]);

  // Build the 12-week visual lane
  const weekSlots = useMemo(() => buildWeekSlots(12), []);
  const slottedByWeek = useMemo(() => {
    const map = {};
    scheduled.forEach(p => {
      if (!p.planned_release_date) return;
      // Find which week-slot this date belongs to (Monday of that week)
      const d = new Date(p.planned_release_date + 'T00:00:00');
      const weekday = d.getDay();
      const offsetBack = weekday === 0 ? 6 : weekday - 1;
      const monday = new Date(d);
      monday.setDate(d.getDate() - offsetBack);
      const mondayKey = monday.toISOString().slice(0, 10);
      if (!map[mondayKey]) map[mondayKey] = [];
      map[mondayKey].push(p);
    });
    return map;
  }, [scheduled]);

  return (
    <div className="min-h-screen bg-background" data-testid="blog-calendar-page">
      <SEO title="Blog Content Calendar — TurfPulse" description="" />
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate('/admin/blog')} className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="blog-calendar-back">
            <ArrowLeft size={14} /> Posts list
          </button>
          <span className="text-sm font-bold flex items-center gap-1.5"><Calendar size={14} weight="fill" /> Content Calendar</span>
          <Button size="sm" onClick={() => navigate('/admin/blog/new')} data-testid="blog-calendar-new">+ New</Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

        {/* 12-week visual lane */}
        {!loading && (
          <section data-testid="blog-calendar-lane">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Next 12 weeks</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {weekSlots.map((iso, idx) => {
                const items = slottedByWeek[iso] || [];
                return (
                  <div key={iso} className="border border-border/40 rounded-md p-2 min-h-[80px] bg-card/40" data-testid={`week-slot-${idx}`}>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Week {idx + 1}</div>
                    <div className="text-[10px] text-muted-foreground mb-1">{fmtDate(iso)}</div>
                    {items.length === 0 ? (
                      <div className="text-[10px] italic text-muted-foreground/60">empty</div>
                    ) : (
                      items.map(p => (
                        <div key={p.slug} className="text-[11px] bg-primary/10 text-primary px-1.5 py-1 rounded mt-1 truncate" title={p.title}>
                          {p.title}
                        </div>
                      ))
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Drafts (no date yet) */}
        {!loading && (
          <section data-testid="blog-calendar-drafts">
            <h2 className="text-sm font-bold mb-2 flex items-center gap-2">
              <PencilSimple size={14} /> Drafts — needs scheduling
              <span className="text-[10px] font-normal text-muted-foreground">({drafts.length})</span>
            </h2>
            {drafts.length === 0 ? (
              <Card><CardContent className="p-4 text-xs text-muted-foreground text-center">All drafts have release dates. 🎯</CardContent></Card>
            ) : (
              <div className="space-y-2">
                {drafts.map(p => (
                  <Card key={p.slug} data-testid={`draft-row-${p.slug}`}>
                    <CardContent className="p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase bg-amber-500/20 text-amber-700 dark:text-amber-400">Draft</span>
                          {p.category && <span className="text-[10px] text-muted-foreground">{p.category}</span>}
                          <span className="text-[10px] text-muted-foreground">{p.read_time_minutes}m</span>
                        </div>
                        <div className="font-semibold text-sm truncate">{p.title}</div>
                        <div className="text-xs text-muted-foreground truncate">{p.excerpt}</div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Input
                          type="date"
                          className="h-8 text-xs w-[140px]"
                          onChange={e => setReleaseDate(p.slug, e.target.value)}
                          data-testid={`draft-date-${p.slug}`}
                        />
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/blog/${p.slug}/edit`)} title="Edit"><PencilSimple size={14} /></Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Scheduled (has date, not published) */}
        {!loading && (
          <section data-testid="blog-calendar-scheduled">
            <h2 className="text-sm font-bold mb-2 flex items-center gap-2">
              <Clock size={14} /> Scheduled
              <span className="text-[10px] font-normal text-muted-foreground">({scheduled.length})</span>
            </h2>
            {scheduled.length === 0 ? (
              <Card><CardContent className="p-4 text-xs text-muted-foreground text-center">Nothing scheduled. Drop dates on your drafts above.</CardContent></Card>
            ) : (
              <div className="space-y-2">
                {scheduled.map(p => (
                  <Card key={p.slug} data-testid={`scheduled-row-${p.slug}`}>
                    <CardContent className="p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase bg-sky-500/20 text-sky-700 dark:text-sky-400">{fmtDate(p.planned_release_date)}</span>
                          {p.category && <span className="text-[10px] text-muted-foreground">{p.category}</span>}
                          <span className="text-[10px] text-muted-foreground">{p.read_time_minutes}m</span>
                        </div>
                        <div className="font-semibold text-sm truncate">{p.title}</div>
                        <div className="text-xs text-muted-foreground truncate">{p.excerpt}</div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Input
                          type="date"
                          defaultValue={p.planned_release_date || ''}
                          className="h-8 text-xs w-[140px]"
                          onChange={e => setReleaseDate(p.slug, e.target.value)}
                          data-testid={`scheduled-date-${p.slug}`}
                        />
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/blog/${p.slug}/edit`)} title="Edit"><PencilSimple size={14} /></Button>
                        <Button size="sm" onClick={() => publish(p.slug)} data-testid={`publish-${p.slug}`}>Publish</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Published */}
        {!loading && (
          <section data-testid="blog-calendar-published">
            <h2 className="text-sm font-bold mb-2 flex items-center gap-2">
              <CheckCircle size={14} weight="fill" className="text-emerald-500" /> Published
              <span className="text-[10px] font-normal text-muted-foreground">({published.length})</span>
            </h2>
            {published.length === 0 ? (
              <Card><CardContent className="p-4 text-xs text-muted-foreground text-center">No published posts yet.</CardContent></Card>
            ) : (
              <div className="space-y-2">
                {published.map(p => (
                  <Card key={p.slug} data-testid={`published-row-${p.slug}`}>
                    <CardContent className="p-3 flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">Live</span>
                          {p.category && <span className="text-[10px] text-muted-foreground">{p.category}</span>}
                          <span className="text-[10px] text-muted-foreground">{p.read_time_minutes}m</span>
                          {p.published_at && <span className="text-[10px] text-muted-foreground">{new Date(p.published_at).toLocaleDateString()}</span>}
                          {typeof p.view_count === 'number' && <span className="text-[10px] text-muted-foreground">{p.view_count} views</span>}
                        </div>
                        <div className="font-semibold text-sm truncate">{p.title}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/blog/${p.slug}`)} title="View"><Eye size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/blog/${p.slug}/edit`)} title="Edit"><PencilSimple size={14} /></Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

export default BlogCalendarPage;
